import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {FormGroup, UntypedFormControl} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';

import {
  BaseDialogComponent,
  OptimizedDataStatus,
  Q9HtmlCleanupPipe,
  REF_MODEL_STORE,
  RefModelStoreService,
  SnackBarService,
  User,
  constants
} from '@q9elements/ui-kit/common';
import {
  CnaDependenciesMediatorService,
  CustomNodeAttributeInterface,
  OptimizeAssignee
} from '@q9elements/ui-kit/refmodels';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {isNull} from 'lodash';
import {Observable, forkJoin, of} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import {RmeNodeDaoAbstractService} from '../../../core/abstract/rme-node-dao-abstract.service';
import {RmeNodeDaoService} from '../../../core/services/rme-node-dao.service';
import {RefModelService} from '../../../services/ref-model.service';
import {IUpdateNodesTagsDialogData, UpdateNodesAction} from './models/update-tags.interface';

const UNASSIGNED = 'Unassigned';

@Component({
  selector: 'app-update-tags-dialog',
  templateUrl: 'update-tags-dialog.component.html',
  styleUrls: ['update-tags-dialog.component.scss']
})
export class UpdateTagsDialogComponent extends BaseDialogComponent implements OnInit {
  public assignees$: Observable<OptimizeAssignee[]>;
  public assigneeFormControl: UntypedFormControl;
  public tags: string[] = [];
  public optimizeStatus: any;
  public optimizeStatuses: any[];
  public error: string;
  public matError: any;
  public selectedAssignee: any;
  public noAssignees: boolean;
  public showUnassigned = false;
  public loading = false;
  public loadingCNADependencies = false;
  public canUpdateCNA = true;
  public customNodeAttribute: CustomNodeAttributeInterface;
  public customNodeAttributes: CustomNodeAttributeInterface[];
  public customNodeAttributeControl: UntypedFormControl;
  public form: FormGroup;

  public get teamId(): string {
    return this.rmeStore.user.userInTeam.team.id;
  }

  public get nodesCount(): number {
    return this.data?.nodeIds?.length || 0;
  }

  public get title(): string {
    return this.data.title;
  }

  public get isDependency(): boolean {
    return this.data?.isDependency;
  }

  public get isTagsUpdate(): boolean {
    return (
      this.data?.action === UpdateNodesAction.TAGS_ADD ||
      this.data?.action === UpdateNodesAction.TAGS_REPLACE
    );
  }

  public get isOptimizationStatusUpdate(): boolean {
    return this.data?.action === UpdateNodesAction.OPTIMIZATION_STATUS_UPDATE;
  }

  public get isNodeAssigneeUpdate(): boolean {
    return this.data?.action === UpdateNodesAction.NODE_ASSIGNEE_UPDATE;
  }

  public get isCNAUpdate(): boolean {
    return this.data?.action === UpdateNodesAction.CNA_UPDATE;
  }

  public get isValid() {
    return (
      (this.tags && this.tags.length >= 1) ||
      !!this.optimizeStatus ||
      (this.assigneeFormControl.value && !this.isErrorState(this.assigneeFormControl)) ||
      (this.customNodeAttribute &&
        this.customNodeAttributeControl?.valid &&
        !this.isErrorState(this.customNodeAttributeControl))
    );
  }

  private get refModel() {
    return this.rmeStore.refModel;
  }

  private get nodesIds() {
    return this.data.nodeIds;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: IUpdateNodesTagsDialogData,
    public dialogRef: MatDialogRef<UpdateTagsDialogComponent>,
    private rmeStore: RefModelStoreService,
    private rmeNodeDaoService: RmeNodeDaoService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private refModelService: RefModelService,
    private q9rmeNodeDaoService: RmeNodeDaoAbstractService,
    private changeDetectorRef: ChangeDetectorRef,
    private cnaDependenciesMediatorService: CnaDependenciesMediatorService,
    private htmlSanitizerPipe: Q9HtmlCleanupPipe
  ) {
    super();
  }

  ngOnInit() {
    this.assigneeFormControl = new UntypedFormControl();
    this.rmeStore
      .select(REF_MODEL_STORE.OPTIMIZE_STATUSES)
      .subscribe((statuses: OptimizedDataStatus[]) => (this.optimizeStatuses = statuses));
    this.setupAssigneesObserver();

    if (this.data.action === UpdateNodesAction.CNA_UPDATE) {
      this.getCustomNodeAttributes();
    }
  }

  private isErrorState(control: UntypedFormControl | null): boolean {
    return !!(control && control.dirty && (control.invalid || this.matError));
  }

  private setupAssigneesObserver(): void {
    this.assignees$ = this.assigneeFormControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      filter((userInput: any) => typeof userInput === 'string'),
      switchMap((userInput: string) => {
        this.selectedAssignee = null;

        if (!userInput || userInput.length < 2) {
          this.matError = 'POPUPS.USER_NOT_SPECIFIED';
          return of([]);
        }

        return this.getOptimizeAssignees(userInput);
      })
    );
  }

  public onSelectAssigneeHandler(event: any) {
    this.matError = '';

    this.selectedAssignee = event;
  }

  public updateTags(event: string[]) {
    this.tags = event;
  }

  override sendSubmitRequest(): Observable<unknown> {
    const {value, field} = this._getValue();
    const payload = {
      nodes: this.nodesIds,
      value,
      ...(this.isCNAUpdate && {field})
    };

    const request = this.rmeNodeDaoService.nodesBulkAction(
      this.refModel._id,
      this.data.action,
      payload
    );

    request.subscribe(
      updatedNodes => {
        const message = this.translate.instant('RUBIC_CUBE.UPDATED_NODES_SUCCESS');

        this.snackBarService.success(message);
        this.dialogRef.close(updatedNodes);
      },
      errorResponse => {
        const message = this.translate.instant('RUBIC_CUBE.UPDATED_NODES_FAILURE');
        const {error} = errorResponse.error;

        console.error('Failed to update tags ', error);

        this.snackBarService.error(error || message);
        this.dialogRef.close();
      }
    );
    return;
  }

  public confirm() {
    if (this.data.action === UpdateNodesAction.CNA_UPDATE && this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.sendSubmitRequest();
  }

  private _getValue() {
    if (this.isTagsUpdate) {
      return {value: this.tags};
    }

    if (this.isOptimizationStatusUpdate) {
      return {value: this.optimizeStatus._id};
    }

    if (this.isNodeAssigneeUpdate) {
      return {value: this.assigneeFormControl.value || null};
    }

    if (this.isCNAUpdate) {
      const isText =
        this.customNodeAttribute.properties.type === constants.customNodeAttributeTypesMap.TEXT;
      const isLongText =
        this.customNodeAttribute.properties.type === constants.customNodeAttributeTypesMap.LONGTEXT;
      const controlValue = this.customNodeAttributeControl.value ?? null;

      if (isText || isLongText) {
        return {
          value: controlValue ? this.htmlSanitizerPipe.transform(controlValue) : null,
          field: this.customNodeAttribute._id
        };
      }
      return {value: controlValue, field: this.customNodeAttribute._id};
    }
  }

  private getOptimizeAssignees(pattern: string): Observable<OptimizeAssignee[]> {
    return this.refModelService.getOptimizeAssignees(this.refModel._id, pattern).pipe(
      tap(response => {
        this.showUnassigned = RegExp(pattern, 'i').test(UNASSIGNED);

        return response;
      }),
      tap(response => {
        this.noAssignees = !response.length;

        if (this.noAssignees && !this.showUnassigned) {
          this.matError = 'POPUPS.USER_NOT_FOUND';
        }
      })
    );
  }

  private getCustomNodeAttributes() {
    this.loading = true;

    forkJoin({
      dependencies: this.q9rmeNodeDaoService.getCustomFieldDependencies(
        this.refModel.implementation as unknown as string
      ),
      customNodeAttributes: this.q9rmeNodeDaoService.getCustomNodeAttributesByNodes(
        this.refModel._id,
        this.data.nodeIds
      )
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(({dependencies, customNodeAttributes}) => {
        this.customNodeAttributes = customNodeAttributes;
        this.cnaDependenciesMediatorService.initCNADependencies(dependencies, customNodeAttributes);
      });
  }

  public onSelectCustomNodeAttribute(cna: CustomNodeAttributeInterface) {
    this.customNodeAttribute = cna;
    this.loadingCNADependencies = true;
    this.rmeNodeDaoService
      .checkForDependents(this.refModel._id, {
        nodeIds: this.data.nodeIds.slice(),
        cnaId: cna._id,
        implementation: cna.implementation
      })
      .pipe(finalize(() => (this.loadingCNADependencies = false)))
      .subscribe(response => {
        this.cnaDependenciesMediatorService.cleanState();

        if (response.changeable) {
          this.cnaDependenciesMediatorService.setAvailableFields(cna._id, response.options || []);
          this.canUpdateCNA = true;
          return;
        }

        this.canUpdateCNA = false;
      });

    const validators = [];

    switch (this.customNodeAttribute.properties.type) {
      case constants.customNodeAttributeTypesMap.TEXT:
        validators.push(Q9Validators.minLength(0), Q9Validators.maxLength(80));

        break;
      case constants.customNodeAttributeTypesMap.LONGTEXT:
        validators.push(Q9Validators.minLength(0), Q9Validators.maxLength(3200));

        break;
      case constants.customNodeAttributeTypesMap.NUMBER:
        if (!isNull(this.customNodeAttribute.properties.min)) {
          validators.push(Q9Validators.min(this.customNodeAttribute.properties.min));
        }

        if (!isNull(this.customNodeAttribute.properties.max)) {
          validators.push(Q9Validators.max(this.customNodeAttribute.properties.max));
        }

        break;
      case constants.customNodeAttributeTypesMap.DATETIME:
        break;
      case constants.customNodeAttributeTypesMap.LIST:
        break;
      case constants.customNodeAttributeTypesMap.MULTI_PICKLIST:
        break;
    }

    // Reset the control to trigger list rendering
    this.customNodeAttributeControl = null;
    this.changeDetectorRef.detectChanges();
    this.customNodeAttributeControl = new UntypedFormControl('', validators);
    this.form = new FormGroup({
      customNodeAttribute: this.customNodeAttributeControl
    });
  }

  public fetchUsers(search?: string): Observable<User[]> {
    const refModelId = this.rmeStore.refModel._id;

    return this.rmeNodeDaoService
      .getOptimizeAssignee(refModelId, search as string)
      .pipe(map(users => users.map(user => ({...user, id: user._id}))));
  }
}
